// InvitationsCustomization.jsx
import { useContext, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import useTranslator from "../../hooks/useTranslator";
import Uploadimg from "../../assets/img/uploaddocoment.png";
import LandLordContext from "../../context/landlord/LandLordContext";
import AuthContext from "../../context/auth/AuthContext";
import LoadingSpinner from "../LoadingSpinner";
import { haveValue, toast } from "../../helper/common";
import Canceling from "../../assets/img/cancel-01.svg";

const InvitationsCustomization = ({
  // --- EDIT mode props ---
  workspaceData,
  onClose,
  // --- CREATE mode props ---
  actionType = "edit", // "edit" | "create"
  invitationWorkspaceData,
  setInvitationWorkspaceData,
  handleSubmitFromParent,
  disableButtons,
  handleGoBackToName,
}) => {
  const { _t } = useTranslator();
  const { user, loadUserTenant } = useContext(AuthContext);
  const {
    updateInvitation,
    checkForDuplicateInvitationSlug,
    deleteInvitationLogo,
    deaWorkspace,
  } = useContext(LandLordContext);

  const isCreateMode = actionType === "create";

  const baseUrl = `${window.location.host}/oauth/`;
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const debounceRef = useRef(null);

  const SLUG_MIN_CHARACTERS = 3;
  const SLUG_MAX_CHARACTERS = 15;

  // ── State ──────────────────────────────────────────────────────────────────
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");

  const [customLinkEnabled, setCustomLinkEnabled] = useState(
    isCreateMode
      ? (invitationWorkspaceData?.customLinkEnabled ?? false)
      : (workspaceData?.customLinkEnabled ?? false)
  );

  const [protectedHousing, setProtectedHousing] = useState(
    isCreateMode
      ? (invitationWorkspaceData?.protectedHousing ?? false) // was hardcoded false
      : (user?.current_workspace?.protected_housing_enabled ?? false)
  );

  const [affidavitActivated, setAffidavitActivated] = useState(
    isCreateMode
      ? (invitationWorkspaceData?.affidavitActivated ?? false) // was hardcoded false
      : (user?.current_workspace?.affidavit_activated ?? false)
  );

  const [deaRulesActivated, setDeaRulesActivated] = useState(
    isCreateMode
      ? (invitationWorkspaceData?.dea_rules_activated ?? false) // was hardcoded false
      : (user?.current_workspace?.dea_rules_activated ?? false)
  );

  const [insuranceEnabled, setInsuranceEnabled] = useState(
    user?.current_workspace?.insurance_service_status ?? false
  );
  const [skorProtectEnabled, setSkorProtectEnabled] = useState(
    user?.current_workspace?.skor_protect_service ?? false
  );

  const [slug, setSlug] = useState(
    isCreateMode
      ? (invitationWorkspaceData?.customLinkSlug ?? "")
      : workspaceData?.customLinkSlug
  );

  const [slugError, setSlugError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [imageDeleteLoading, setImageDeleteLoading] = useState(false);

  const initialProtectedHousing =
    user?.current_workspace?.protected_housing_enabled ?? false;
  const initialAffidavitActivated =
    user?.current_workspace?.affidavit_activated ?? false;
  const initialDeaRulesActivated =
    user?.current_workspace?.dea_rules_activated ?? false;
  const initialInsuranceEnabled =
    user?.current_workspace?.insurance_service_status ?? false;
  const initialSkorProtectEnabled =
    user?.current_workspace?.skor_protect_service ?? false;

  // ── Slug change handler ────────────────────────────────────────────────────
  const handleChange = (e) => {
    let sanitized = e.target.value.replace(/[^a-zA-Z0-9-_]/g, "");
    if (sanitized.length > SLUG_MAX_CHARACTERS) {
      sanitized = sanitized.slice(0, SLUG_MAX_CHARACTERS);
    }
    setSlug(sanitized);
    setSlugTouched(true);
    if (sanitized !== slug) setSlugAvailable(null);
    setSlugChecking(false);
    setSlugError("");
  };

  // ── Slug debounce / validation ─────────────────────────────────────────────
  useEffect(() => {
    if (!customLinkEnabled || !slugTouched) return;

    clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!slug) {
        setSlugError(_t("enterSlugToContinue"));
        setSlugAvailable(null);
        return;
      }
      if (slug.length < SLUG_MIN_CHARACTERS) {
        setSlugError(_t("slugMinCharactersLimitNotMatched"));
        setSlugAvailable(null);
        return;
      }
      if (slug.length > SLUG_MAX_CHARACTERS) {
        setSlugError(_t("slugMaxCharactersLimitReached"));
        setSlugAvailable(null);
        return;
      }

      // In edit mode skip API call if slug unchanged
      const existingSlug = isCreateMode
        ? invitationWorkspaceData?.customLinkSlug
        : workspaceData?.customLinkSlug;

      if (slug === existingSlug) {
        setSlugError("");
        setSlugAvailable(true);
        return;
      }

      try {
        setSlugChecking(true);
        setSlugError("");
        const res = await checkForDuplicateInvitationSlug({
          customLinkSlug: slug,
        });
        if (res?.success) {
          setSlugAvailable(true);
        } else {
          setSlugError(_t("slugAlreadyInSystemPleaseTrySomethingElse"));
          setSlugAvailable(false);
        }
      } catch {
        setSlugError(_t("slugAlreadyInSystemPleaseTrySomethingElse"));
        setSlugAvailable(false);
      } finally {
        setSlugChecking(false);
      }
    }, 600);

    return () => clearTimeout(debounceRef.current);
  }, [slug, customLinkEnabled]);

  // ── File helpers ───────────────────────────────────────────────────────────
  const validateFile = async (file) => {
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      setError(_t("fileTypeNotAllowed", "Only PNG or JPG files allowed"));
      return false;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError(_t("fileUploadedExceedsTwoMbLimit", "File exceeds 2MB limit"));
      return false;
    }
    try {
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Corrupted image"));
        img.src = URL.createObjectURL(file);
      });
    } catch {
      setError(
        _t("isCorruptedOrUnreadableFile", "File is corrupted or unreadable")
      );
      return false;
    }
    setError("");
    return true;
  };

  const processFile = async (file) => {
    if (!file) return;
    const isValid = await validateFile(file);
    if (isValid) setSelectedFile(file);
  };

  const handleFileChange = (e) => processFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    processFile(e.dataTransfer.files[0]);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (dragCounter.current === 1) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const removeFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setError("");
  };

  // ── Change detection ───────────────────────────────────────────────────────
  const hasChanges = isCreateMode
    ? true // always allow continuing in create mode
    : !!selectedFile ||
      customLinkEnabled !== workspaceData?.customLinkEnabled ||
      slug !== workspaceData?.customLinkSlug ||
      protectedHousing !== initialProtectedHousing ||
      affidavitActivated !== initialAffidavitActivated ||
      deaRulesActivated !== initialDeaRulesActivated ||
      insuranceEnabled !== initialInsuranceEnabled ||
      skorProtectEnabled !== initialSkorProtectEnabled;

  const canSubmit =
    !error &&
    hasChanges &&
    !slugChecking &&
    (!customLinkEnabled || slugAvailable !== false);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!canSubmit) return;

    if (customLinkEnabled) {
      if (!slug) {
        setSlugError(_t("enterSlugToContinue"));
        return;
      }
      if (slug.length < SLUG_MIN_CHARACTERS) {
        setSlugError(_t("slugMinCharactersLimitNotMatched"));
        return;
      }
      if (slug.length > SLUG_MAX_CHARACTERS) {
        setSlugError(_t("slugMaxCharactersLimitReached"));
        return;
      }
    }

    // ── CREATE mode: push data up to parent, then advance step ──────────────
    if (isCreateMode) {
      setInvitationWorkspaceData({
        customLinkEnabled,
        customLinkSlug: customLinkEnabled ? slug : "",
        invitationLogo: selectedFile,
        protectedHousing, // ← add this
        affidavitActivated, // ← add this
        dea_rules_activated: deaRulesActivated,
      });
      handleSubmitFromParent();
      return;
    }

    // ── EDIT mode: call API directly ─────────────────────────────────────────
    try {
      setSubmitLoading(true);
      const formData = new FormData();
      if (selectedFile) formData.append("invitationLogo", selectedFile);
      formData.append("customLinkEnabled", customLinkEnabled ?? false);
      formData.append("affidavit_activated", affidavitActivated);
      formData.append("dea_rules_activated", deaRulesActivated);
      formData.append("protected_housing_enabled", protectedHousing);
      if (!isCreateMode && user?.insurance_service_status) {
        formData.append("insurance_service_status", insuranceEnabled);
      }
      if (!isCreateMode && user?.skor_protect_service) {
        formData.append("skor_protect_service", skorProtectEnabled);
      }
      if (customLinkEnabled) formData.append("customLinkSlug", slug);

      await updateInvitation(formData);
      await loadUserTenant("landlord");
      onClose();
      toast.success(_t("invitationSettingsUpdatedSuccessfully"));
    } catch (err) {
      console.error("Error uploading:", err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteInvitationLink = async () => {
    if (imageDeleteLoading) return;
    try {
      setImageDeleteLoading(true);
      await deleteInvitationLogo();
      await loadUserTenant("landlord");
      toast.success(_t("invitationLogoRemovedSuccessfully"));
    } catch {
      toast.error(_t("failedToRemoveInvitationLogo"));
    } finally {
      setImageDeleteLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  // In create mode the component is rendered inline (no Modal wrapper needed).
  // In edit mode it is already inside a Modal, so we keep Modal.Body.
  const existingLogo =
    !isCreateMode && haveValue(workspaceData?.invitationLogo);

  const body = (
    <div className="invitation-tab">
      {/* Custom Invitation Link */}
      <div className="d-flex justify-content-between align-items-center custom-mt-3">
        <div className="outer-label">
          <label>{_t("customInvitationLink")}</label>
        </div>
        <label className="custom-toggle-wrapper">
          <input
            type="checkbox"
            checked={customLinkEnabled}
            onChange={() => setCustomLinkEnabled((v) => !v)}
          />
          <span className="custom-toggle-slider" />
        </label>
      </div>

      {customLinkEnabled && (
        <>
          <p className="company-name-text">
            {_t("useABrandedUrlSlugWhenInvitingTenants")}
          </p>
          <div
            className={`d-flex align-items-center url-wrapper custom-mt-3 ${slugError ? "error" : ""}`}
          >
            <span>{baseUrl}</span>
            <input
              type="text"
              className="form-control"
              value={slug}
              onChange={handleChange}
              style={{ borderRadius: "0 6px 6px 0" }}
            />
            {slugChecking && (
              <LoadingSpinner
                size={"0.8233vw"}
                color="#253837"
                borderWidth={1}
                className="docs-loading-spinner"
              />
            )}
            {!slugChecking &&
              slugTouched &&
              slugAvailable !== null &&
              slug.length >= SLUG_MIN_CHARACTERS && (
                <img
                  src={
                    slugAvailable
                      ? "/images/checkmark-green.svg"
                      : "/images/close.svg"
                  }
                  className={`url-wrapper-img ${slugAvailable ? "" : "red-icon"}`}
                  alt="status"
                />
              )}
          </div>
          {slugError && <p className="error-message mt-1">{slugError}</p>}
        </>
      )}

      {/* Invitation Logo */}
      <div className="outer-label invitation-logo-margin">
        <label>{_t("invitationLogo")}</label>
        <span className="optional-logo-styling">{_t("optional")}</span>
      </div>
      <p className="company-name-text modal-margin-bottom">
        {_t("shownToTenantsWhenTheyReceiveAnInvitation")}
      </p>

      {existingLogo ? (
        <div className="invitation-relative">
          <div className="invitation-logo-preview">
            <img
              height={100}
              width={100}
              src={`${process.env.REACT_APP_CDN_ENDPOINT}/${process.env.REACT_APP_CDN_CONTAINER}/${workspaceData?.invitationLogo}`}
            />
            <div
              onClick={handleDeleteInvitationLink}
              className="cursor-pointer invitation-logo-delete-icon"
            >
              {imageDeleteLoading ? (
                <LoadingSpinner
                  size={"0.8233vw"}
                  color="#253837"
                  borderWidth={1}
                  className="docs-loading-spinner"
                />
              ) : (
                <img src={Canceling} alt="icon" />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div
          className="doc-listing doc-listing2 doc-listing3 h-100 d-flex align-items-center justify-content-center text-center cursor-pointer"
          onClick={() => !selectedFile && fileInputRef.current.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          style={{ borderColor: error ? "#fd0000" : "" }}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/png,image/jpeg,image/jpg"
            style={{ display: "none" }}
          />

          {selectedFile ? (
            <div className="uploaded-files-view d-flex align-items-center justify-content-between w-100">
              <div className="uploaded-files-view-name d-flex align-items-center gap-3">
                <img
                  src={URL.createObjectURL(selectedFile)}
                  alt="preview"
                  style={{
                    width: 50,
                    height: 50,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
                <div className="fileSizeText">
                  <h6 className="mb-0 text-truncate" style={{ maxWidth: 200 }}>
                    {selectedFile.name}
                  </h6>
                  <small>{(selectedFile.size / 1024).toFixed(1)} KB</small>
                </div>
              </div>
              <div
                className="cursor-pointer uploaded-files-view-delete-icon"
                onClick={removeFile}
              >
                ✕
              </div>
            </div>
          ) : (
            <div>
              <div className="Uploadimgdiv">
                <img src={Uploadimg} alt="upload" width={40} />
              </div>
              <p className="drag-drop-content pt-3">
                {_t("dragAndDropYourLogoHere")}
              </p>
              <p className="spacepx">{_t("or")}</p>
              <button
                type="button"
                className="btnNext btnSelectFile selectFilehere"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current.click();
                }}
              >
                {_t("selectFromFiles")}
              </button>
              <p className="fileSizeText">
                {_t("pngJpgUpTo2MbRecommended256X256")}
              </p>
            </div>
          )}
        </div>
      )}

      {error && <p className="error-message mt-1">{error}</p>}

      {/* Protected Housing — only shown in edit mode */}
      {/* {!isCreateMode && ( */}
      <div>
        <div className="d-flex justify-content-between align-items-center custom-mt-3">
          <div className="outer-label inner-span-style">
            <label>{_t("protectedHousing")}</label>
          </div>
          <label className="custom-toggle-wrapper custom-toggle-wrapper-invitation">
            <input
              type="checkbox"
              checked={protectedHousing}
              onChange={() => setProtectedHousing((v) => !v)}
            />
            <span className="custom-toggle-slider" />
          </label>
        </div>
        <p className="company-name-text company-name-width">
          {_t("protectedHousingTogglerDescriptionInInvitationPopup")}
        </p>
      </div>
      {/* )} */}

      {/* Affidavit — only shown in edit mode */}
      {/* {!isCreateMode && ( */}
      <div>
        <div className="d-flex justify-content-between align-items-center custom-mt-3">
          <div className="outer-label inner-span-style">
            <label>{_t("fundsDeclarationAffidavit")}</label>
          </div>
          <label className="custom-toggle-wrapper custom-toggle-wrapper-invitation">
            <input
              type="checkbox"
              checked={affidavitActivated}
              onChange={() => setAffidavitActivated((v) => !v)}
            />
            <span className="custom-toggle-slider" />
          </label>
        </div>
        <p className="company-name-text company-name-width">
          {_t("fundsDeclarationAffidavitTogglerDescriptionInInvitationPopup")}
        </p>
      </div>
      {/* )} */}

      {/* Insurance — only shown in edit mode */}
      {!isCreateMode && user?.insurance_service_status && (
        <div>
          <div className="d-flex justify-content-between align-items-center custom-mt-3">
            <div className="outer-label inner-span-style">
              <label>{_t("insuranceToggle")}</label>
            </div>
            <label className="custom-toggle-wrapper custom-toggle-wrapper-invitation">
              <input
                type="checkbox"
                checked={insuranceEnabled}
                onChange={() => {
                  const next = !insuranceEnabled;
                  setInsuranceEnabled(next);
                  if (!next) setSkorProtectEnabled(false);
                }}
              />
              <span className="custom-toggle-slider" />
            </label>
          </div>
          <p className="company-name-text company-name-width">
            {_t("insuranceTogglerDescriptionInInvitationPopup")}
          </p>
        </div>
      )}

      {/* Skor Protect — only shown in edit mode */}
      {!isCreateMode && user?.skor_protect_service && (
        <div>
          <div className="d-flex justify-content-between align-items-center custom-mt-3">
            <div className="outer-label inner-span-style">
              <label>{_t("skorProtectToggle")}</label>
            </div>
            <label className="custom-toggle-wrapper custom-toggle-wrapper-invitation">
              <input
                type="checkbox"
                checked={skorProtectEnabled}
                onChange={() => {
                  const next = !skorProtectEnabled;
                  setSkorProtectEnabled(next);
                  if (next) setInsuranceEnabled(true);
                }}
              />
              <span className="custom-toggle-slider" />
            </label>
          </div>
          <p className="company-name-text company-name-width">
            {_t("skorProtectTogglerDescriptionInInvitationPopup")}
          </p>
        </div>
      )}

      {/* Toggle for DEA created workspaces only */}
      {((actionType == "create" &&
        user?._id?.toString() === "691368fee4b283b07c7d33c2") ||
        (actionType == "edit" && deaWorkspace)) && (
        <div>
          <div className="d-flex justify-content-between align-items-center custom-mt-3">
            <div className="outer-label inner-span-style">
              <label>{_t("enableDeaRules")}</label>
            </div>
            <label className="custom-toggle-wrapper custom-toggle-wrapper-invitation">
              <input
                type="checkbox"
                checked={deaRulesActivated}
                onChange={() => setDeaRulesActivated((v) => !v)}
              />
              <span className="custom-toggle-slider" />
            </label>
          </div>
          <p className="company-name-text company-name-width">
            {_t("enableDeaRulesTogglerDescriptionInInvitationPopup")}
          </p>
        </div>
      )}
    </div>
  );

  // ── Create mode: render inline with parent-controlled button ───────────────
  if (isCreateMode) {
    return (
      <>
        {body}
        <div className="inviteButtonWrapper">
          <button
            type="button"
            className="btnNext btnnextpre mr-2"
            onClick={handleGoBackToName}
          >
            {_t("goBack")}
          </button>
          <button
            type="button"
            className="btnNext basicInfoButton greenHover"
            onClick={handleSubmit}
          >
            {_t("continue")}
          </button>
        </div>
      </>
    );
  }

  // ── Edit mode: keep original Modal.Body + footer button ───────────────────
  return (
    <>
      <Modal.Body className="invitation-tab">{body}</Modal.Body>
      <div className="inviteButtonWrapper">
        <button
          type="button"
          className="btnNext basicInfoButton greenHover"
          disabled={!canSubmit || submitLoading}
          onClick={handleSubmit}
        >
          {_t(submitLoading ? "submitting" : "continue")}
        </button>
      </div>
    </>
  );
};

export default InvitationsCustomization;
